/**
 * Identify traffic that arrived from an AI answer engine.
 *
 * WHY THIS EXISTS: the GEO work on this site (FAQPage coverage, the entity graph, machine-readable
 * comparison tables, real author attribution) is all aimed at being cited by ChatGPT, Perplexity,
 * Claude, Copilot and Google AI Overviews -- and until now none of it was measurable. Without a
 * classifier there is no way to tell whether any of it paid off, which makes the whole programme
 * unfalsifiable.
 *
 * WHY CLIENT-SIDE: every content page here is static/ISR and served from the Cloudflare edge, so
 * there is no per-request server code to inspect the `Referer` header -- a cache HIT never reaches
 * the origin. Reading `document.referrer` in the browser is the only place that sees every visit.
 * (Middleware would run per-request but would defeat the CDN caching this site depends on.)
 *
 * This module is intentionally PURE: no fetch, no storage, no side effects. That keeps it unit
 * testable and safe to import from either side of the client boundary. The transport lives in
 * `src/components/analytics/AiReferralBeacon.tsx` and `src/app/api/ai-referral/route.ts`.
 *
 * NOTE: deliberately standalone -- it predates the real trackers in `src/utils/*Tracking.ts`
 * and has its own transport and table.
 */

/** Canonical id for an AI surface. Stable -- these become log/analytics keys. */
export type AiSourceId =
  | "chatgpt"
  | "perplexity"
  | "claude"
  | "copilot"
  | "gemini"
  | "google-ai-overview"
  | "you"
  | "poe"
  | "meta-ai"
  | "mistral"
  | "deepseek"
  | "grok"
  | "brave-leo"
  | "arc-search"
  | "other-ai";

export interface AiReferral {
  source: AiSourceId;
  /** Human label for dashboards. */
  label: string;
  /**
   * How we identified it:
   *  - `referrer`  the browser sent a referring host we recognise (most reliable)
   *  - `utm`       an explicit utm_source/utm_medium we or the engine set
   *  - `param`     a bare marker param such as ?ref=chatgpt.com
   */
  via: "referrer" | "utm" | "param";
  /** The raw evidence, for debugging a misclassification later. */
  evidence: string;
}

/**
 * Referring hosts, most specific first.
 *
 * Matched against the hostname only, as a suffix, so `chat.openai.com` matches `openai.com` and a
 * lookalike like `openai.com.evil.test` does not. Bare `google.com` is NOT here: ordinary Google
 * search is not an AI referral, and AI Overviews are not distinguishable by referrer (see
 * GOOGLE_AI_NOTE below).
 */
const REFERRER_HOSTS: Array<[suffix: string, id: AiSourceId, label: string]> = [
  ["chatgpt.com", "chatgpt", "ChatGPT"],
  ["chat.openai.com", "chatgpt", "ChatGPT"],
  ["openai.com", "chatgpt", "ChatGPT / OpenAI"],
  ["perplexity.ai", "perplexity", "Perplexity"],
  ["claude.ai", "claude", "Claude"],
  ["copilot.microsoft.com", "copilot", "Microsoft Copilot"],
  ["bing.com/chat", "copilot", "Microsoft Copilot"],
  ["gemini.google.com", "gemini", "Google Gemini"],
  ["bard.google.com", "gemini", "Google Gemini"],
  ["you.com", "you", "You.com"],
  ["poe.com", "poe", "Poe"],
  ["meta.ai", "meta-ai", "Meta AI"],
  ["chat.mistral.ai", "mistral", "Mistral"],
  ["lechat.mistral.ai", "mistral", "Mistral"],
  ["chat.deepseek.com", "deepseek", "DeepSeek"],
  ["grok.com", "grok", "Grok"],
  ["x.ai", "grok", "Grok"],
  ["search.brave.com", "brave-leo", "Brave Leo"],
  ["arc.net", "arc-search", "Arc Search"],
];

/** utm_source / ref values engines and campaigns actually use. Matched case-insensitively. */
const PARAM_VALUES: Array<[needle: string, id: AiSourceId, label: string]> = [
  ["chatgpt", "chatgpt", "ChatGPT"],
  ["openai", "chatgpt", "ChatGPT / OpenAI"],
  ["oai-searchbot", "chatgpt", "ChatGPT"],
  ["perplexity", "perplexity", "Perplexity"],
  ["claude", "claude", "Claude"],
  ["anthropic", "claude", "Claude"],
  ["copilot", "copilot", "Microsoft Copilot"],
  ["bingchat", "copilot", "Microsoft Copilot"],
  ["gemini", "gemini", "Google Gemini"],
  ["bard", "gemini", "Google Gemini"],
  ["google-ai", "google-ai-overview", "Google AI Overview"],
  ["ai-overview", "google-ai-overview", "Google AI Overview"],
  ["you.com", "you", "You.com"],
  ["poe", "poe", "Poe"],
  ["meta.ai", "meta-ai", "Meta AI"],
  ["mistral", "mistral", "Mistral"],
  ["deepseek", "deepseek", "DeepSeek"],
  ["grok", "grok", "Grok"],
];

/**
 * GOOGLE_AI_NOTE: Google AI Overviews send the SAME referrer as ordinary Google search
 * (google.com/*), so referrer alone cannot separate them -- do not add google.com above hoping to
 * catch them. AI Overview traffic is only identifiable via Search Console's dedicated reporting or
 * an explicit campaign parameter, which is why `google-ai-overview` is reachable through params
 * only. Claiming otherwise would produce a confidently wrong number.
 */

function hostOf(raw: string): string | null {
  try {
    return new URL(raw).hostname.replace(/^www\./, "").toLowerCase();
  } catch {
    return null;
  }
}

/** Suffix match on host boundaries, so "openai.com.evil.test" cannot match "openai.com". */
function hostMatches(host: string, suffix: string): boolean {
  const s = suffix.split("/")[0]; // entries like "bing.com/chat" carry a path hint
  return host === s || host.endsWith("." + s);
}

/**
 * Classify a visit. Returns null for ordinary traffic -- callers should send nothing in that case
 * rather than recording a "direct" row for every pageview.
 *
 * @param referrer  document.referrer (or a Referer header), may be empty
 * @param url       the landing URL, so its query string can be inspected
 */
export function classifyAiReferral(
  referrer: string | null | undefined,
  url: string | null | undefined,
): AiReferral | null {
  // 1. Referrer host -- the strongest signal, and set by the engine rather than by us.
  const host = referrer ? hostOf(referrer) : null;
  if (host) {
    for (const [suffix, id, label] of REFERRER_HOSTS) {
      if (hostMatches(host, suffix)) {
        // A path hint ("bing.com/chat") must also appear, or plain Bing search would match.
        const pathHint = suffix.includes("/") ? suffix.slice(suffix.indexOf("/")) : null;
        if (pathHint && !referrer!.toLowerCase().includes(pathHint)) continue;
        return { source: id, label, via: "referrer", evidence: host };
      }
    }
  }

  // 2. Explicit parameters. Checked second so a real referrer always wins, and scoped to the
  //    params that carry provenance -- scanning the whole query string would match any page whose
  //    slug happens to contain "claude" or "grok".
  const params = url ? safeParams(url) : null;
  if (params) {
    for (const key of ["utm_source", "utm_medium", "utm_campaign", "ref", "source", "via"]) {
      const value = params.get(key);
      if (!value) continue;
      const v = value.toLowerCase();
      for (const [needle, id, label] of PARAM_VALUES) {
        if (v.includes(needle)) {
          return { source: id, label, via: key.startsWith("utm_") ? "utm" : "param", evidence: `${key}=${value}` };
        }
      }
    }
  }

  return null;
}

function safeParams(url: string): URLSearchParams | null {
  try {
    return new URL(url, "https://hirebee.ai").searchParams;
  } catch {
    return null;
  }
}

/** Every id, for building a dashboard/report skeleton with no gaps. */
export const AI_SOURCE_IDS: AiSourceId[] = [
  "chatgpt", "perplexity", "claude", "copilot", "gemini", "google-ai-overview",
  "you", "poe", "meta-ai", "mistral", "deepseek", "grok", "brave-leo", "arc-search", "other-ai",
];
